import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FeatureService, type Feature, type FeatureEntity } from '../../DynamicEntities/admin/services/FeatureService';
import { useOptionalDynamicEntitiesContext } from '../../DynamicEntities/core/DynamicEntitiesProvider';
import { isDynamicEntitiesHttpError } from '../../DynamicEntities/core/api/DynamicEntitiesHttpError';
import type { DynamicEntitiesRef } from '../../DynamicEntities/ui/DynamicEntities';
import type {
  DynamicEntitiesGrainConfig,
  DynamicEntitiesTemplateConfig,
} from '../template/MasterDocumentTemplate.types';

/** Sufijo por defecto de cada grano. Espeja los que siembra el backend del host. */
const DEFAULT_SUFFIX = { header: 'HEADER', line: 'LINE' } as const;

export type DocumentDynamicGrainKey = keyof typeof DEFAULT_SUFFIX;

/**
 * Un grano ya resuelto: el Feature existe, tiene entidades renderizables y sus
 * refs se pueden registrar y guardar.
 */
export interface DocumentDynamicGrain {
  /** Código del Feature realmente consultado. */
  featureCode: string;
  /** Entidades a pintar, ya filtradas y ordenadas. */
  entities: FeatureEntity[];
  /** Título del bloque tal como lo declaró el template (crudo). */
  label?: string;
  /** Clave i18n del título declarada por el template. */
  labelKey?: string;
  /** Registra (o desregistra, con `null`) el ref imperativo de una entidad. */
  registerRef: (entityCode: string, ref: DynamicEntitiesRef | null) => void;
  /**
   * Persiste los valores de TODAS las entidades registradas contra `recordId`.
   * Nunca lanza: un fallo se reporta por el `onError` del provider y vuelve
   * como `false`.
   */
  save: (recordId: string) => Promise<boolean>;
  /** Olvida los refs registrados. Se llama al cerrar el modal de línea. */
  clearRefs: () => void;
}

export interface DocumentDynamicEntitiesHandle {
  /** `true` solo si hay provider Y el template declaró al menos un grano. */
  enabled: boolean;
  header: DocumentDynamicGrain | null;
  line: DocumentDynamicGrain | null;
}

interface UseDocumentDynamicEntitiesParams {
  config?: DynamicEntitiesTemplateConfig;
  /** Clase del documento en curso — la del estado, no la del template. */
  documentClass: string;
}

/** Normaliza `true | false | {…}` a la forma de objeto, o `null` si está apagado. */
function normalizeGrain(
  grain: boolean | DynamicEntitiesGrainConfig | undefined,
): DynamicEntitiesGrainConfig | null {
  if (!grain) return null;
  return grain === true ? {} : grain;
}

function resolveFeatureCode(
  grain: DynamicEntitiesGrainConfig,
  key: DocumentDynamicGrainKey,
  documentClass: string,
): string | null {
  if (grain.featureCode) return grain.featureCode;
  if (!documentClass) return null;
  return `${documentClass}_${grain.suffix ?? DEFAULT_SUFFIX[key]}`;
}

/**
 * Resuelve los campos adicionales (EAV) de un documento y orquesta su guardado.
 *
 * ## Por qué el Feature se consulta y no se asume
 *
 * El template solo dice *que* el documento admite campos adicionales; **cuáles**
 * son lo decide el administrador en la pantalla de Entidades Dinámicas, y puede
 * cambiar sin desplegar. Por eso se pide el Feature por código en runtime y se
 * pinta lo que traiga: un Feature sin entidades ⇒ ningún bloque, exactamente como
 * un documento que no declara la capacidad.
 *
 * ## Degradaciones deliberadas (ninguna rompe el documento)
 *
 * - **Sin `DynamicEntitiesProvider`** ⇒ `enabled: false` y todo inerte. Es la
 *   situación de cualquier host que aún no cableó la capacidad, y de todos los
 *   tests y stories que montan el documento suelto.
 * - **Feature inexistente (404)** ⇒ se trata como "esta clase no tiene campos
 *   adicionales". Es el caso normal de una clase cuyo grano el backend no siembra
 *   (el catálogo está acotado por los flags de la clase), no un error que merezca
 *   un toast al usuario.
 * - **Cualquier otro fallo** ⇒ se reporta por el `onError` del provider y el
 *   grano queda vacío. El documento sigue guardándose.
 *
 * ## Modales, fuera
 *
 * De los tres `renderMode` del paquete se pintan `inline` y `tab`. `modal` se
 * ignora a propósito: el documento ya tiene su propia capa de modales (línea,
 * acciones, cruce) y abrir otro nivel desde dentro del formulario de captura es
 * una trampa de foco, no una funcionalidad.
 */
export function useDocumentDynamicEntities({
  config,
  documentClass,
}: UseDocumentDynamicEntitiesParams): DocumentDynamicEntitiesHandle {
  const context = useOptionalDynamicEntitiesContext();
  const baseUrl = context?.baseUrl;
  const fetcher = context?.fetcher;
  const onError = context?.onError;

  const headerConfig = normalizeGrain(config?.header);
  const lineConfig = normalizeGrain(config?.line);

  const headerCode = headerConfig ? resolveFeatureCode(headerConfig, 'header', documentClass) : null;
  const lineCode = lineConfig ? resolveFeatureCode(lineConfig, 'line', documentClass) : null;

  const service = useMemo(
    () => (baseUrl ? new FeatureService(baseUrl, fetcher) : null),
    [baseUrl, fetcher],
  );

  const [features, setFeatures] = useState<Record<string, Feature | null>>({});

  // Los códigos a resolver, como una clave estable: sin esto el efecto se
  // re-dispara en cada render porque el array es nuevo cada vez.
  const wantedCodes = [headerCode, lineCode].filter((c): c is string => !!c);
  const wantedKey = wantedCodes.join('|');

  useEffect(() => {
    if (!service || wantedKey === '') return;

    let alive = true;
    const codes = wantedKey.split('|');

    Promise.all(
      codes.map(async (code) => {
        try {
          return [code, await service.getByCode(code)] as const;
        } catch (err) {
          // 404 = esta clase no tiene ese grano sembrado. Es información, no un
          // fallo: se cachea como `null` para no volver a preguntar.
          if (isDynamicEntitiesHttpError(err) && err.status === 404) {
            return [code, null] as const;
          }
          onError?.(err as Error);
          return [code, null] as const;
        }
      }),
    ).then((entries) => {
      if (!alive) return;
      setFeatures((prev) => {
        const next = { ...prev };
        for (const [code, feature] of entries) next[code] = feature;
        return next;
      });
    });

    return () => {
      alive = false;
    };
  }, [service, wantedKey, onError]);

  // Un registro de refs por grano. Vive en un ref y no en estado: registrar un
  // hijo no debe re-renderizar al padre, y el guardado los lee en el momento del
  // submit, no en el del render.
  const headerRefs = useRef<Record<string, DynamicEntitiesRef | null>>({});
  const lineRefs = useRef<Record<string, DynamicEntitiesRef | null>>({});

  const makeGrain = useCallback(
    (
      code: string | null,
      grainConfig: DynamicEntitiesGrainConfig | null,
      refs: React.MutableRefObject<Record<string, DynamicEntitiesRef | null>>,
    ): DocumentDynamicGrain | null => {
      if (!service || !code || !grainConfig) return null;
      const feature = features[code];
      if (!feature) return null;

      const entities = (feature.featureEntities ?? [])
        .filter((fe) => !!fe.dynamicEntity && fe.renderMode !== 'modal')
        .sort((a, b) => a.order - b.order);

      if (entities.length === 0) return null;

      return {
        featureCode: code,
        entities,
        label: grainConfig.label,
        labelKey: grainConfig.labelKey,
        registerRef: (entityCode, ref) => {
          if (ref) refs.current[entityCode] = ref;
          else delete refs.current[entityCode];
        },
        save: async (recordId: string) => {
          const active = Object.values(refs.current).filter((r): r is DynamicEntitiesRef => !!r);
          if (active.length === 0) return true;
          // `DynamicEntities.save` ya captura sus propios fallos y devuelve
          // false, así que aquí no hay nada que reventar; el `catch` es por si
          // una versión futura decide lanzar.
          const results = await Promise.all(
            active.map(async (ref) => {
              try {
                return await ref.save(recordId);
              } catch (err) {
                onError?.(err as Error);
                return false;
              }
            }),
          );
          return !results.includes(false);
        },
        clearRefs: () => {
          refs.current = {};
        },
      };
    },
    [service, features, onError],
  );

  const header = useMemo(
    () => makeGrain(headerCode, headerConfig, headerRefs),
    // headerConfig es un objeto nuevo en cada render (normalizeGrain); sus campos
    // son los que importan y son primitivos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeGrain, headerCode, headerConfig?.label, headerConfig?.labelKey],
  );

  const line = useMemo(
    () => makeGrain(lineCode, lineConfig, lineRefs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeGrain, lineCode, lineConfig?.label, lineConfig?.labelKey],
  );

  return {
    enabled: !!service && (!!header || !!line),
    header,
    line,
  };
}
